import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, map, switchMap, catchError, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthUser, Credentials, Role } from '../models/auth-user.model';

@Injectable({ providedIn: 'root' })
export class IamService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiGatewayUrl}/iam`;

  authenticate(credentials: Readonly<Credentials>): Observable<AuthUser | null> {
    return this.http
      .post<AuthUser>(`${this.baseUrl}/users/login`, {
        email: credentials.email,
        password: credentials.password,
      })
      .pipe(
        switchMap((user) =>
          this.clientOptions(user.token).pipe(map((options) => ({ ...user, options }) as AuthUser)),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401 || err.status === 403) {
            return of(null);
          }
          return throwError(() => err);
        }),
      );
  }

  me(token?: string): Observable<AuthUser> {
    const meOptions = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return this.http.get<AuthUser>(`${this.baseUrl}/users/me`, meOptions).pipe(
      switchMap((user) =>
        this.clientOptions(token).pipe(
          map((options) => {
            if (!user.division) {
              user.roles = [...user.roles, Role.ADMIN];
            }
            return { ...user, options } as AuthUser;
          }),
        ),
      ),
    );
  }

  requestResetPassword(email: string): Observable<void> {
    let params = new HttpParams().set('email', email);
    params = params.set('scope', 'tmdigital');
    return this.http.post<void>(`${this.baseUrl}/passwords/requests`, null, { params });
  }

  validatePasswordResetRequest(oobToken: string): Observable<string> {
    return this.http
      .get<{ email: string }>(`${this.baseUrl}/passwords/requests/${oobToken}`)
      .pipe(map((res) => res.email));
  }

  changeCredentials(oobToken: string, credentials: Credentials): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/passwords/requests/${oobToken}`, {
      email: credentials.email,
      password: credentials.password,
    });
  }

  clientOptions(token?: string): Observable<Record<string, unknown>> {
    const options = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return this.http.get<Record<string, unknown>>(`${this.baseUrl}/clients/options`, options);
  }
}
